import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, X, Truck, GasPump, Wrench } from "@phosphor-icons/react";

const TAB_LIST = ["vehicles", "trips", "fuel", "maintenance"];

export default function FleetMgmt() {
  const [tab, setTab] = useState("vehicles");
  return (
    <AppShell title="Fleet Management" subtitle="Module 8">
      <div className="flex gap-1 mb-6 border-b border-[#E5E2D9]">
        {TAB_LIST.map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`fleet-tab-${t}`}
                  className={`px-5 py-2.5 text-sm capitalize border-b-2 transition-colors ${tab === t ? "border-[#2D5A27] text-[#1A1A1A] font-medium" : "border-transparent text-[#5C5A55]"}`}>
            {t}
          </button>
        ))}
      </div>
      {tab === "vehicles" && <Vehicles/>}
      {tab === "trips" && <Trips/>}
      {tab === "fuel" && <Fuel/>}
      {tab === "maintenance" && <Maintenance/>}
    </AppShell>
  );
}

function Vehicles() {
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    vehicle_no: "", type: "4_wheeler", capacity_kg: 0, status: "active",
    assigned_staff_id: "", current_odometer: 0,
  });

  const load = async () => {
    const [v, s] = await Promise.all([api.get("/vehicles"), api.get("/staff")]);
    setItems(v.data); setStaff(s.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ vehicle_no: "", type: "4_wheeler", capacity_kg: 0, status: "active",
              assigned_staff_id: "", current_odometer: 0 });
    setOpen(true);
  };
  const openEdit = (v) => {
    setEditing(v.id);
    setForm({ ...v, assigned_staff_id: v.assigned_staff_id || "" });
    setOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        vehicle_no: form.vehicle_no, type: form.type,
        capacity_kg: Number(form.capacity_kg),
        status: form.status,
        assigned_staff_id: form.assigned_staff_id || null,
        current_odometer: Number(form.current_odometer),
      };
      if (editing) await api.put(`/vehicles/${editing}`, payload);
      else await api.post("/vehicles", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete vehicle?")) return;
    try { await api.delete(`/vehicles/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const staffName = (id) => staff.find(s => s.id === id)?.name || "—";

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md">
      <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center justify-between">
        <div className="flex items-center gap-2"><Truck size={18} color="#2D5A27" weight="fill"/><span className="font-heading">Vehicles</span></div>
        <button data-testid="btn-new-vehicle" onClick={openNew} className="h-9 px-3 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-xs inline-flex items-center gap-1.5"><Plus size={12} weight="bold"/>Add</button>
      </div>
      <table className="w-full text-sm" data-testid="vehicles-table">
        <thead>
          <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
            <th className="px-6 py-3">No</th><th className="px-6 py-3">Type</th>
            <th className="px-6 py-3">Capacity</th><th className="px-6 py-3">Odometer</th>
            <th className="px-6 py-3">Assigned to</th><th className="px-6 py-3">Status</th>
            <th className="px-6 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={7} className="px-6 py-10 text-center text-[#8A8780]">No vehicles.</td></tr>}
          {items.map((v, i) => (
            <tr key={v.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
              <td className="px-6 py-3 font-mono font-medium">{v.vehicle_no}</td>
              <td className="px-6 py-3">{v.type}</td>
              <td className="px-6 py-3 font-mono">{v.capacity_kg} kg</td>
              <td className="px-6 py-3 font-mono text-[#5C5A55]">{v.current_odometer} km</td>
              <td className="px-6 py-3 text-[#5C5A55]">{staffName(v.assigned_staff_id)}</td>
              <td className="px-6 py-3"><span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded bg-[#D4E0D1] text-[#1A3317]">{v.status}</span></td>
              <td className="px-6 py-3 text-right">
                <button onClick={() => openEdit(v)} className="text-[#2D5A27] text-xs mr-3 inline-flex items-center gap-1"><PencilSimple size={12}/>Edit</button>
                <button onClick={() => del(v.id)} className="text-[#C25D38] text-xs inline-flex items-center gap-1"><Trash size={12}/>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading text-lg">{editing ? "Edit" : "New"} vehicle</div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
            </div>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="vehicle-form">
              <Field label="Vehicle no" value={form.vehicle_no} onChange={v=>setForm(f=>({...f,vehicle_no:v}))} required testid="vf-no"/>
              <Sel label="Type" value={form.type} onChange={v=>setForm(f=>({...f,type:v}))}
                   options={[
                     {value:"2_wheeler",label:"2 Wheeler"},
                     {value:"3_wheeler",label:"3 Wheeler"},
                     {value:"4_wheeler",label:"4 Wheeler"},
                     {value:"6_wheeler_truck",label:"6 Wheeler Truck"},
                   ]}/>
              <Field label="Capacity (kg)" type="number" value={form.capacity_kg} onChange={v=>setForm(f=>({...f,capacity_kg:v}))} testid="vf-capacity"/>
              <Field label="Current odometer (km)" type="number" value={form.current_odometer} onChange={v=>setForm(f=>({...f,current_odometer:v}))} testid="vf-odometer"/>
              <Sel label="Assigned staff" value={form.assigned_staff_id} onChange={v=>setForm(f=>({...f,assigned_staff_id:v}))}
                   options={[{value:"",label:"— none —"}, ...staff.map(s=>({value:s.id,label:s.name}))]}/>
              <Sel label="Status" value={form.status} onChange={v=>setForm(f=>({...f,status:v}))}
                   options={["active","inactive","maintenance"].map(r=>({value:r,label:r}))}/>
              <button data-testid="vf-save" type="submit" className="col-span-2 h-10 bg-[#2D5A27] text-white rounded-md text-sm font-medium">Save</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Trips() {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({
    vehicle_id: "", staff_id: "", start_time: "", end_time: "",
    odometer_start: 0, odometer_end: "", notes: "",
  });

  const load = async () => {
    const [t, v, s] = await Promise.all([api.get("/trips"), api.get("/vehicles"), api.get("/staff")]);
    setRows(t.data); setVehicles(v.data); setStaff(s.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/trips", {
        vehicle_id: form.vehicle_id,
        staff_id: form.staff_id || null,
        start_time: form.start_time,
        end_time: form.end_time || null,
        odometer_start: Number(form.odometer_start),
        odometer_end: form.odometer_end ? Number(form.odometer_end) : null,
        notes: form.notes,
      });
      toast.success("Trip logged");
      setForm({ ...form, start_time: "", end_time: "", odometer_start: 0, odometer_end: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const vn = (id) => vehicles.find(v => v.id === id)?.vehicle_no || "—";
  const sn = (id) => staff.find(s => s.id === id)?.name || "—";

  return (
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2 bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E2D9] font-heading">Trip log</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Start</th><th className="px-6 py-3">Vehicle</th>
              <th className="px-6 py-3">Staff</th><th className="px-6 py-3">Distance</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="px-6 py-10 text-center text-[#8A8780]">No trips.</td></tr>}
            {rows.map((r,i) => (
              <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                <td className="px-6 py-3 font-mono text-xs">{r.start_time}</td>
                <td className="px-6 py-3">{vn(r.vehicle_id)}</td>
                <td className="px-6 py-3">{sn(r.staff_id)}</td>
                <td className="px-6 py-3 font-mono">{r.distance ? `${r.distance} km` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
        <div className="font-heading text-lg mb-4">Log a trip</div>
        <form onSubmit={submit} className="space-y-3" data-testid="trip-form">
          <Sel label="Vehicle" value={form.vehicle_id} onChange={v=>setForm(f=>({...f,vehicle_id:v}))}
               options={[{value:"",label:"Select"}, ...vehicles.map(v=>({value:v.id,label:v.vehicle_no}))]}/>
          <Sel label="Staff" value={form.staff_id} onChange={v=>setForm(f=>({...f,staff_id:v}))}
               options={[{value:"",label:"—"}, ...staff.map(s=>({value:s.id,label:s.name}))]}/>
          <Field label="Start" value={form.start_time} onChange={v=>setForm(f=>({...f,start_time:v}))} required/>
          <Field label="End" value={form.end_time} onChange={v=>setForm(f=>({...f,end_time:v}))}/>
          <Field label="Odometer start" type="number" value={form.odometer_start} onChange={v=>setForm(f=>({...f,odometer_start:v}))} required/>
          <Field label="Odometer end" type="number" value={form.odometer_end} onChange={v=>setForm(f=>({...f,odometer_end:v}))}/>
          <button type="submit" data-testid="trip-save" className="w-full h-10 bg-[#2D5A27] text-white rounded-md text-sm font-medium">Save</button>
        </form>
      </div>
    </div>
  );
}

function Fuel() { return <SimpleLog kind="fuel"/>; }
function Maintenance() { return <SimpleLog kind="maintenance"/>; }

function SimpleLog({ kind }) {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(kind === "fuel"
    ? { vehicle_id: "", date: "", amount: 0, liters: 0 }
    : { vehicle_id: "", service_date: "", next_due: "", cost: 0, notes: "" });

  const endpoint = kind === "fuel" ? "/fuel" : "/maintenance";

  const load = async () => {
    const [r, v] = await Promise.all([api.get(endpoint), api.get("/vehicles")]);
    setRows(r.data); setVehicles(v.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      ["amount", "liters", "cost"].forEach(k => { if (k in payload) payload[k] = Number(payload[k]); });
      await api.post(endpoint, payload);
      toast.success("Saved");
      setForm(kind === "fuel"
        ? { vehicle_id: form.vehicle_id, date: "", amount: 0, liters: 0 }
        : { vehicle_id: form.vehicle_id, service_date: "", next_due: "", cost: 0, notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const vn = (id) => vehicles.find(v => v.id === id)?.vehicle_no || "—";
  const Icon = kind === "fuel" ? GasPump : Wrench;

  return (
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2 bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-2"><Icon size={16} color="#2D5A27" weight="fill"/><div className="font-heading capitalize">{kind} log</div></div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Date</th><th className="px-6 py-3">Vehicle</th>
              {kind === "fuel" ? <>
                <th className="px-6 py-3">Amount</th><th className="px-6 py-3">Liters</th>
              </> : <>
                <th className="px-6 py-3">Cost</th><th className="px-6 py-3">Next due</th>
              </>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="px-6 py-10 text-center text-[#8A8780]">No records.</td></tr>}
            {rows.map((r,i)=>(
              <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                <td className="px-6 py-3 font-mono text-xs">{r.date || r.service_date}</td>
                <td className="px-6 py-3">{vn(r.vehicle_id)}</td>
                {kind === "fuel" ? <>
                  <td className="px-6 py-3 font-mono">₹{r.amount}</td>
                  <td className="px-6 py-3 font-mono">{r.liters}L</td>
                </> : <>
                  <td className="px-6 py-3 font-mono">₹{r.cost}</td>
                  <td className="px-6 py-3 font-mono text-xs">{r.next_due || "—"}</td>
                </>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
        <div className="font-heading text-lg mb-4">Add {kind}</div>
        <form onSubmit={submit} className="space-y-3" data-testid={`${kind}-form`}>
          <Sel label="Vehicle" value={form.vehicle_id} onChange={v=>setForm(f=>({...f,vehicle_id:v}))}
               options={[{value:"",label:"Select"}, ...vehicles.map(v=>({value:v.id,label:v.vehicle_no}))]}/>
          {kind === "fuel" ? <>
            <Field label="Date" value={form.date} onChange={v=>setForm(f=>({...f,date:v}))} required/>
            <Field label="Amount (₹)" type="number" value={form.amount} onChange={v=>setForm(f=>({...f,amount:v}))} required/>
            <Field label="Liters" type="number" value={form.liters} onChange={v=>setForm(f=>({...f,liters:v}))} required/>
          </> : <>
            <Field label="Service date" value={form.service_date} onChange={v=>setForm(f=>({...f,service_date:v}))} required/>
            <Field label="Next due" value={form.next_due} onChange={v=>setForm(f=>({...f,next_due:v}))}/>
            <Field label="Cost (₹)" type="number" value={form.cost} onChange={v=>setForm(f=>({...f,cost:v}))}/>
            <Field label="Notes" value={form.notes} onChange={v=>setForm(f=>({...f,notes:v}))}/>
          </>}
          <button type="submit" data-testid={`${kind}-save`} className="w-full h-10 bg-[#2D5A27] text-white rounded-md text-sm font-medium">Save</button>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, required, type="text", testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{label}</label>
    <input data-testid={testid} type={type} required={required} value={value}
           onChange={e => onChange(e.target.value)}
           className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
  </div>
);
const Sel = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)}
            className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);
